import { useState, useEffect, useMemo, useRef } from 'react';
import { createPortal } from 'react-dom';
import { Link } from 'react-router-dom';
import { AnimatePresence, animate, motion, useMotionValue, useTransform } from 'framer-motion';
import { X } from 'lucide-react';
import { Product, ProductVariant } from '../../../lib/api/models';
import api from '../../../lib/api';
import { useAuth } from '../../../context/AuthContext';
import { useCart } from '../../../context/CartContext';

const getVariantLabel = (variant: ProductVariant) => {
  return variant.name || variant.sku;
};

const getQuantityRules = (variant: ProductVariant | null) => {
  const minimumQuantity = Math.max(1, Number(variant?.packaging?.minimumQuantity) || 1);
  const quantityStep = Math.max(1, Number(variant?.packaging?.quantityStep) || 1);

  return { minimumQuantity, quantityStep };
};

const FeaturedProductCartControl = ({ product }: { product: Product }) => {
  const { auth } = useAuth();
  const { addItem } = useCart();
  const controlRef = useRef<HTMLDivElement>(null);
  const sheetY = useMotionValue(1000);
  const variants = useMemo(
    () => (product.variants || []).filter((variant) => variant.isActive !== false),
    [product.variants]
  );
  const hasVariants = variants.length > 0;
  const [expanded, setExpanded] = useState(false);
  const [sheetExpanded, setSheetExpanded] = useState(false);
  const [viewportHeight, setViewportHeight] = useState(
    typeof window === 'undefined' ? 800 : window.innerHeight
  );
  const [selectedVariantSku, setSelectedVariantSku] = useState(variants[0]?.sku || '');
  const selectedVariant = hasVariants
    ? variants.find((variant) => variant.sku === selectedVariantSku) || variants[0]
    : null;
  const { minimumQuantity, quantityStep } = getQuantityRules(selectedVariant);
  const [quantity, setQuantity] = useState(minimumQuantity);
  const [isAdding, setIsAdding] = useState(false);
  const [success, setSuccess] = useState(false);

  useEffect(() => {
    setSelectedVariantSku(variants[0]?.sku || '');
  }, [variants]);

  useEffect(() => {
    const rules = getQuantityRules(selectedVariant);
    setQuantity(rules.minimumQuantity);
  }, [selectedVariant]);

  useEffect(() => {
    if (!expanded) return;

    const handlePointerDown = (event: PointerEvent) => {
      if (window.matchMedia('(max-width: 767px)').matches) return;
      const target = event.target;
      if (target instanceof Node && !controlRef.current?.contains(target)) {
        setExpanded(false);
      }
    };

    document.addEventListener('pointerdown', handlePointerDown);
    return () => document.removeEventListener('pointerdown', handlePointerDown);
  }, [expanded]);

  useEffect(() => {
    if (!expanded) return;

    const updateViewportHeight = () => setViewportHeight(window.innerHeight);
    updateViewportHeight();
    window.addEventListener('resize', updateViewportHeight);
    return () => window.removeEventListener('resize', updateViewportHeight);
  }, [expanded]);

  const compactSheetHeight = Math.min(
    hasVariants ? 440 : 320,
    Math.max(280, viewportHeight - 96)
  );
  const expandedSheetTop = Math.round(viewportHeight * 0.08);
  const compactSheetTop = Math.max(expandedSheetTop, viewportHeight - compactSheetHeight);
  const compactOpacity = useTransform(sheetY, [expandedSheetTop, compactSheetTop], [0, 1]);
  const detailOpacity = useTransform(sheetY, [expandedSheetTop, compactSheetTop], [1, 0]);
  const detailScale = useTransform(sheetY, [expandedSheetTop, compactSheetTop], [1, 0.96]);
  const compactScale = useTransform(sheetY, [expandedSheetTop, compactSheetTop], [0.97, 1]);

  const snapSheetTo = (nextExpanded: boolean) => {
    setSheetExpanded(nextExpanded);
    animate(sheetY, nextExpanded ? expandedSheetTop : compactSheetTop, {
      type: 'spring',
      damping: 30,
      stiffness: 280,
      mass: 0.9,
    });
  };

  useEffect(() => {
    if (!expanded) return;
    sheetY.set(viewportHeight);
    snapSheetTo(false);
  }, [expanded, viewportHeight]);

  if (!auth.isAuthenticated) {
    return null;
  }

  const changeQuantity = (direction: -1 | 1) => {
    setQuantity((current) => Math.max(minimumQuantity, current + direction * quantityStep));
  };

  const handleAdd = async () => {
    if (isAdding) return;

    setIsAdding(true);
    try {
      await Promise.resolve(addItem(product, selectedVariant, quantity));
      setSuccess(true);
      setExpanded(false);
      setSheetExpanded(false);
      window.setTimeout(() => setSuccess(false), 1600);
    } catch (error) {
      console.error('Error al añadir producto destacado al carrito:', error);
    } finally {
      setIsAdding(false);
    }
  };

  const variantOptions = hasVariants ? (
    <div className="space-y-2">
      <p className="text-xs font-semibold uppercase tracking-wide text-gray-500">Variante</p>
      <div className="grid gap-2">
        {variants.map((variant) => {
          const selected = variant.sku === selectedVariant?.sku;
          return (
            <button
              key={variant.sku}
              type="button"
              className={`rounded-xl border px-3 py-2 text-left transition-colors ${selected
                ? 'border-black bg-black text-white'
                : 'border-gray-200 bg-white text-gray-900 hover:border-gray-400'
                }`}
              onClick={() => setSelectedVariantSku(variant.sku)}
            >
              <span className="block truncate text-sm font-semibold">{getVariantLabel(variant)}</span>
              {variant.sku && (
                <span className={`mt-0.5 block text-[11px] ${selected ? 'text-white/70' : 'text-gray-500'}`}>
                  {variant.sku}
                </span>
              )}
            </button>
          );
        })}
      </div>
    </div>
  ) : null;

  const quantityControls = (
    <div className="flex items-center gap-2">
      <div className="flex h-10 items-center rounded-full border border-gray-200 bg-gray-50">
        <button
          type="button"
          className="h-10 w-10 text-sm font-bold text-gray-700 disabled:text-gray-300"
          onClick={() => changeQuantity(-1)}
          disabled={quantity <= minimumQuantity}
          aria-label="Reducir cantidad"
        >
          -
        </button>
        <span className="min-w-9 text-center text-sm font-semibold">{quantity}</span>
        <button
          type="button"
          className="h-10 w-10 text-sm font-bold text-gray-700"
          onClick={() => changeQuantity(1)}
          aria-label="Aumentar cantidad"
        >
          +
        </button>
      </div>

      <button
        type="button"
        className="h-10 flex-1 rounded-full bg-black px-4 text-xs font-semibold text-white transition-colors hover:bg-gray-800 disabled:cursor-wait disabled:bg-gray-500"
        onClick={handleAdd}
        disabled={isAdding}
      >
        {isAdding ? 'Añadiendo' : 'Añadir'}
      </button>
    </div>
  );

  const openControl = () => {
    setSheetExpanded(false);
    setExpanded(true);
  };

  const closeControl = () => {
    setExpanded(false);
    setSheetExpanded(false);
    sheetY.set(viewportHeight);
  };

  const handleSheetDragEnd = (_event: MouseEvent | TouchEvent | PointerEvent, info: { velocity: { y: number } }) => {
    const currentY = sheetY.get();
    const midpoint = expandedSheetTop + (compactSheetTop - expandedSheetTop) * 0.52;

    if (info.velocity.y < -450) {
      snapSheetTo(true);
      return;
    }

    if (info.velocity.y > 700 && currentY > compactSheetTop + 36) {
      closeControl();
      return;
    }

    if (info.velocity.y > 450) {
      snapSheetTo(false);
      return;
    }

    snapSheetTo(currentY < midpoint);
  };

  return (
    <div
      ref={controlRef}
      className="absolute bottom-4 right-4 z-20"
      onClick={(event) => {
        event.preventDefault();
        event.stopPropagation();
      }}
    >
      <motion.div layout className="bg-white text-black shadow-lg ring-1 ring-black/10 rounded-full md:hidden">
        <button
          type="button"
          className="flex h-11 w-11 items-center justify-center transition-colors hover:bg-gray-100"
          aria-label="Añadir al carrito"
          onClick={openControl}
        >
          {success ? (
            <svg className="h-5 w-5 text-green-700" fill="currentColor" viewBox="0 0 20 20" aria-hidden="true">
              <path fillRule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clipRule="evenodd" />
            </svg>
          ) : (
            <svg className="h-5 w-5" fill="none" stroke="currentColor" viewBox="0 0 24 24" aria-hidden="true">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 3h2l.4 2M7 13h10l4-8H5.4M7 13L5.4 5M7 13l-2.293 2.293c-.63.63-.184 1.707.707 1.707H17m0 0a2 2 0 100 4 2 2 0 000-4zm-8 2a2 2 0 11-4 0 2 2 0 014 0z" />
            </svg>
          )}
        </button>
      </motion.div>

      <div className="relative hidden md:block">
        <motion.div
          layout
          className={`absolute bottom-0 right-0 overflow-hidden bg-white text-black shadow-xl ring-1 ring-black/10 ${expanded ? 'w-[min(18rem,calc(100vw-4rem))] rounded-2xl p-3' : 'h-11 w-11 rounded-full p-0'}`}
          transition={{ layout: { type: 'spring', damping: 28, stiffness: 360 } }}
        >
          {!expanded ? (
            <motion.button
              layout
              type="button"
              className="flex h-11 w-11 items-center justify-center transition-colors hover:bg-gray-100"
              aria-label="Añadir al carrito"
              onClick={openControl}
            >
              {success ? (
                <svg className="h-5 w-5 text-green-700" fill="currentColor" viewBox="0 0 20 20" aria-hidden="true">
                  <path fillRule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clipRule="evenodd" />
                </svg>
              ) : (
                <svg className="h-5 w-5" fill="none" stroke="currentColor" viewBox="0 0 24 24" aria-hidden="true">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 3h2l.4 2M7 13h10l4-8H5.4M7 13L5.4 5M7 13l-2.293 2.293c-.63.63-.184 1.707.707 1.707H17m0 0a2 2 0 100 4 2 2 0 000-4zm-8 2a2 2 0 11-4 0 2 2 0 014 0z" />
                </svg>
              )}
            </motion.button>
          ) : (
            <motion.div
              layout
              className="space-y-3"
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              transition={{ duration: 0.12, delay: 0.06 }}
            >
              {variantOptions}
              <div className="flex items-center gap-2">
                <div className="flex-1">{quantityControls}</div>
                <button
                  type="button"
                  className="h-10 w-10 shrink-0 rounded-full text-gray-500 transition-colors hover:bg-gray-100 hover:text-black"
                  onClick={closeControl}
                  aria-label="Cerrar selector"
                >
                  <X className="mx-auto h-4 w-4" aria-hidden="true" />
                </button>
              </div>
            </motion.div>
          )}
        </motion.div>
      </div>

      {typeof document !== 'undefined' && createPortal(
        <AnimatePresence>
          {expanded && (
            <motion.div
              className="fixed inset-0 z-[9999] md:hidden"
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              exit={{ opacity: 0 }}
            >
              <button
                type="button"
                className="absolute inset-0 h-full w-full bg-black/45"
                aria-label="Cerrar añadir al carrito"
                onClick={closeControl}
              />
              <motion.div
                className="absolute inset-x-0 top-0 flex h-[100dvh] flex-col rounded-t-3xl bg-white text-black shadow-2xl will-change-transform"
                style={{ y: sheetY }}
                initial={{ y: viewportHeight }}
                exit={{ y: '100%' }}
                drag="y"
                dragConstraints={{ top: expandedSheetTop, bottom: compactSheetTop + 120 }}
                dragElastic={{ top: 0.08, bottom: 0.18 }}
                onDragEnd={handleSheetDragEnd}
              >
                <div className="shrink-0 px-5 pt-3">
                  <button
                    type="button"
                    className="mx-auto mb-4 block h-5 w-16 rounded-full"
                    aria-label={sheetExpanded ? 'Contraer panel' : 'Expandir panel'}
                    onClick={() => snapSheetTo(!sheetExpanded)}
                  >
                    <span className="mx-auto block h-1 w-11 rounded-full bg-gray-300" />
                  </button>
                  <div className="mb-3 flex items-center justify-between">
                    <p className="text-xs font-semibold uppercase tracking-wide text-gray-500">
                      {sheetExpanded ? 'Detalle del producto' : 'Añadir al carrito'}
                    </p>
                    <button
                      type="button"
                      className="flex h-9 w-9 shrink-0 items-center justify-center rounded-full bg-gray-100 text-gray-600"
                      onClick={closeControl}
                      aria-label="Cerrar selector"
                    >
                      <X className="h-4 w-4" aria-hidden="true" />
                    </button>
                  </div>
                </div>

                <div className="relative min-h-0 flex-1 pb-32">
                  <motion.div
                    className="absolute inset-0 snap-y snap-mandatory overflow-y-auto overscroll-contain px-5 pb-36"
                    style={{ opacity: detailOpacity, scale: detailScale }}
                    aria-hidden={!sheetExpanded}
                  >
                    <section className="snap-start pb-6">
                      <div className="overflow-hidden rounded-2xl bg-gray-100">
                        <img
                          src={product.images[0]?.url || '/placeholder-product.jpg'}
                          alt={product.name}
                          className="aspect-square w-full object-cover"
                        />
                      </div>
                      <h3 className="mt-4 text-xl font-bold leading-tight text-gray-950">{product.name}</h3>
                      {product.description && (
                        <p className="mt-2 line-clamp-3 text-sm leading-relaxed text-gray-600">
                          {product.description}
                        </p>
                      )}
                    </section>

                    {hasVariants && (
                      <section className="snap-start pb-6">
                        {variantOptions}
                      </section>
                    )}

                    <section className="snap-start pb-10">
                      <p className="text-xs font-semibold uppercase tracking-wide text-gray-500">Cantidad</p>
                      <div className="mt-3 rounded-2xl border border-gray-200 bg-gray-50 p-4">
                        <p className="text-sm font-semibold text-gray-950">
                          {selectedVariant ? getVariantLabel(selectedVariant) : product.name}
                        </p>
                        {minimumQuantity > 1 || quantityStep > 1 ? (
                          <p className="mt-1 text-xs text-gray-500">
                            Mínimo {minimumQuantity} · Paso {quantityStep}
                          </p>
                        ) : (
                          <p className="mt-1 text-xs text-gray-500">Selecciona la cantidad para añadir.</p>
                        )}
                      </div>
                    </section>
                  </motion.div>

                  <motion.div
                    className="absolute inset-x-0 top-0 space-y-4 px-5 pb-36"
                    style={{ opacity: compactOpacity, scale: compactScale }}
                    aria-hidden={sheetExpanded}
                  >
                    <div>
                      <h3 className="line-clamp-2 text-base font-bold text-gray-950">{product.name}</h3>
                      <p className="mt-1 text-xs text-gray-500">Arrastra hacia arriba para ver más detalles.</p>
                    </div>
                    {hasVariants && variantOptions}
                  </motion.div>
                </div>
              </motion.div>

              <div className="fixed inset-x-0 bottom-0 z-[10000] bg-white px-5 pb-[calc(env(safe-area-inset-bottom)+2.25rem)] pt-4 shadow-[0_-12px_32px_rgba(0,0,0,0.12)]">
                {quantityControls}
              </div>
            </motion.div>
          )}
        </AnimatePresence>,
        document.body
      )}
    </div>
  );
};

const FeaturedProducts = () => {
  const [products, setProducts] = useState<Product[]>([]);
  const [loading, setLoading] = useState(true);

  // Efecto para cargar productos
  useEffect(() => {
    const fetchProducts = async () => {
      try {
        const response = await api.client.get<Product[]>('/products?featured=true&limit=100');
        // El cliente API ya extrae 'data' del backend, así que response.data es el array de productos
        const data = response.success && response.data ? response.data : [];
        console.log('Productos destacados cargados:', data);
        setProducts(Array.isArray(data) ? data : []);
      } catch (error) {
        console.error('Error al cargar productos destacados:', error);
      } finally {
        setLoading(false);
      }
    };

    fetchProducts();
  }, []);

  // Animaciones para elementos
  const containerVariants = {
    hidden: { opacity: 0 },
    visible: {
      opacity: 1,
      transition: {
        staggerChildren: 0.1
      }
    }
  };

  const itemVariants = {
    hidden: { opacity: 0 },
    visible: {
      opacity: 1,
      transition: {
        duration: 0.25,
        ease: "easeOut"
      }
    }
  };

  if (loading) {
    return (
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-8">
        {[...Array(4)].map((_, index) => (
          <div key={index} className="animate-pulse">
            <div className="bg-gray-200 aspect-w-1 aspect-h-1 rounded-lg overflow-hidden mb-4"></div>
            <div className="h-4 bg-gray-200 rounded w-3/4 mb-2"></div>
            <div className="h-4 bg-gray-200 rounded w-1/2"></div>
          </div>
        ))}
      </div>
    );
  }

  return (
    <div>
      {/* Productos con tarjetas cuadradas, alineadas con promociones */}
      <motion.div
        variants={containerVariants}
        initial="hidden"
        animate="visible"
        className="flex gap-4 overflow-x-auto snap-x snap-mandatory"
      >
        {products.length === 0 ? (
          <div className="w-full text-center py-12">
            <p className="text-gray-500">No hay productos disponibles.</p>
          </div>
        ) : (
          products.map((product) => (
            <motion.div
              key={product.id}
              variants={itemVariants}
              className="group relative w-[calc((100vw-3rem)/2)] max-w-none shrink-0 snap-start md:w-[22rem]"
              data-testid="product-card"
            >
              <Link
                to={`/products/${product.id}`}
                className="relative block aspect-square overflow-hidden rounded-xl bg-gray-100 transition-shadow duration-300 group-hover:shadow-xl"
              >
                {/* Badge de nuevo o destacado */}
                <div className="absolute top-4 left-4 z-10">
                  <div className="px-3 py-1.5 text-xs font-medium bg-black text-white rounded-full tracking-wide">
                    Destacado
                  </div>
                </div>

                {/* Imagen principal con zoom en hover */}
                <motion.img
                  src={product.images[0]?.url || '/placeholder-product.jpg'}
                  alt={product.name}
                  className="absolute inset-0 h-full w-full object-cover"
                  whileHover={{ scale: 1.05 }}
                  transition={{ duration: 0.4 }}
                  data-testid="product-image"
                />

                <div className="absolute inset-x-0 bottom-0 z-10 bg-gradient-to-t from-black/80 via-black/45 to-transparent p-4 pt-16 text-white">
                  <h3 className="line-clamp-2 text-sm font-semibold md:text-base" data-testid="product-name">
                    {product.name}
                  </h3>

                  <div className="mt-2 flex flex-wrap gap-1.5">
                    {product.variants?.slice(0, 2).map((variant) => (
                      <span
                        key={variant.sku || variant.id}
                        className="max-w-full truncate rounded bg-white/90 px-2 py-1 text-[10px] font-medium text-gray-900"
                      >
                        {variant.name}
                      </span>
                    ))}
                    {product.variants && product.variants.length > 2 && (
                      <span className="rounded bg-white/90 px-2 py-1 text-[10px] font-medium text-gray-900">
                        +{product.variants.length - 2}
                      </span>
                    )}
                  </div>

                  <div className="mt-2" data-testid="product-price">
                    {(() => {
                      // Obtener precio: del producto, o de la primera variante
                      let displayPrice = product.price;
                      let promoPrice = (product as any).originalPrice;
                      let hasPromo = (product as any).hasPromo;

                      // Si no hay precio a nivel de producto, usar primera variante
                      if (!displayPrice && product.variants && product.variants.length > 0) {
                        const firstVariant = product.variants[0] as any;
                        displayPrice = firstVariant.price || firstVariant.prices?.tarifa1 || 0;
                        promoPrice = firstVariant.originalPrice;
                        hasPromo = firstVariant.hasPromo;
                      }

                      return hasPromo && promoPrice ? (
                        <div className="flex items-baseline gap-2">
                          <span className="text-lg font-semibold text-white">
                            {(displayPrice || 0).toFixed(2)}€
                          </span>
                          <span className="text-sm text-white/65 line-through">
                            {promoPrice.toFixed(2)}€
                          </span>
                        </div>
                      ) : (
                        <span className="text-lg font-semibold text-white">
                          {displayPrice ? `${displayPrice.toFixed(2)}€` : 'Consultar'}
                        </span>
                      );
                    })()}
                  </div>
                </div>
              </Link>
              <FeaturedProductCartControl product={product} />
            </motion.div>
          ))
        )}
      </motion.div>

      {/* Botón para ver más productos */}
      <div className="mt-16 text-center">
        <Link
          to="/products"
          className="inline-flex items-center px-6 py-3 border-2 border-black text-base font-medium rounded-full shadow-sm text-white bg-black hover:bg-[#f6cf02] hover:border-black hover:text-black transition-colors"
        >
          Ir a tienda
          <svg xmlns="http://www.w3.org/2000/svg" className="ml-2 -mr-1 h-5 w-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 7l5 5m0 0l-5 5m5-5H6" />
          </svg>
        </Link>
      </div>
    </div>
  );
};

export default FeaturedProducts; 
